import type { UserDocumentFilterVariables } from '@/modules/users'
import { endOfDay, startOfDay } from 'date-fns'
import type { UserDocumentListFilterForm } from './types'

/**
 * Map the page's filter state to query variables, dropping everything that is
 * not set so an untouched filter sends no arguments at all. The export mutation
 * takes the same shape, so it can never disagree with the table.
 */
export function getUserDocumentFilterVariables(
  filter: UserDocumentListFilterForm
): UserDocumentFilterVariables {
  const q = filter.q.trim()

  return {
    q: q || undefined,
    users: filter.users.length > 0 ? filter.users : undefined,
    categories: filter.categories.length > 0 ? filter.categories : undefined,
    responsibleUsers:
      filter.responsibleUsers.length > 0 ? filter.responsibleUsers : undefined,
    expirationStatus: filter.expirationStatus || undefined,
    // Picking today as the end of the range should include a document dated
    // later today, so the picked days are widened to their full extent.
    expirationDateGte: filter.dateRange?.from
      ? startOfDay(filter.dateRange.from).toISOString()
      : undefined,
    expirationDateLte: filter.dateRange?.to
      ? endOfDay(filter.dateRange.to).toISOString()
      : undefined,
  }
}

/** Whether any filter is narrowing the list — drives the "clear" button. */
export function hasActiveUserDocumentFilter(
  filter: UserDocumentListFilterForm
): boolean {
  return (
    filter.q.trim().length > 0 ||
    filter.users.length > 0 ||
    filter.categories.length > 0 ||
    filter.responsibleUsers.length > 0 ||
    filter.expirationStatus !== '' ||
    Boolean(filter.dateRange?.from || filter.dateRange?.to)
  )
}
